/**
 * Formatos oficiales mexicanos para campos del wizard de captura.
 *
 * Fuentes:
 *  - CURP: DOF 18-oct-2018 «ACUERDO por el que se da a conocer el
 *    Instructivo Normativo para la Asignación de la CURP».
 *  - RFC: SAT — Reglamento del CFF, art. 27. Persona física: 4 letras +
 *    AAMMDD + 3 alfanuméricos. Persona moral: 3 letras + AAMMDD + 3
 *    alfanuméricos.
 *  - CLABE: Banxico — Circular 4/2003 (formato y dígito verificador
 *    weighted mod 10 con pesos [3,7,1]).
 *  - Clave de elector INE: 18 chars en el frente de la credencial (6
 *    consonantes + 8 dígitos fecha+entidad + sexo H/M + 3 dígitos
 *    diferenciadores). Distinta del CIC (9 dígitos al reverso) y del
 *    OCR (13 dígitos).
 *  - Teléfono MX: 10 dígitos sin LADA país (post Plan Nacional 2019).
 *
 * Cada validator retorna `{ valid, reason? }`. `reason` es un mensaje
 * legible para mostrar inline al user. Las normalizers son seguras de
 * llamar sobre input vacío (devuelven '').
 */

export interface ValidationResult {
  valid: boolean;
  /** Mensaje legible cuando valid=false. undefined cuando valid=true. */
  reason?: string;
}

const OK: ValidationResult = { valid: true };

/* ════════════════════════════════════════════════════════════════════════
   CURP — 18 caracteres
   Estructura: 4 letras + AAMMDD + sexo (H/M) + 2 letras estado + 3
   consonantes + homoclave + dígito verificador
   ──────────────────────────────────────────────────────────────────────── */

/** Catálogo oficial RENAPO de 32 entidades + NE (extranjero). */
const CURP_ENTIDADES = [
  'AS','BC','BS','CC','CL','CM','CS','CH','DF','DG','GT','GR','HG',
  'JC','MC','MN','MS','NT','NL','OC','PL','QT','QR','SP','SL','SR',
  'TC','TS','TL','VZ','YN','ZS','NE',
];

const CURP_REGEX = new RegExp(
  '^[A-Z][AEIOUX][A-Z]{2}'                    // 4 letras (1ª apellido, vocal interna, 2º apellido, nombre)
  + '\\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\\d|3[01])' // AAMMDD
  + '[HM]'                                     // sexo
  + '(' + CURP_ENTIDADES.join('|') + ')'      // entidad
  + '[B-DF-HJ-NP-TV-Z]{3}'                    // 3 consonantes internas
  + '[0-9A-Z]'                                 // homoclave (dígito o letra)
  + '\\d$',                                    // dígito verificador
);

export function normalizeCURP(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).trim().toUpperCase().replace(/\s+/g, '');
}

export function validateCURP(input: string | null | undefined): ValidationResult {
  const v = normalizeCURP(input);
  if (!v) return OK; // empty == válido (campo opcional)
  if (v.length !== 18) {
    return { valid: false, reason: `CURP debe tener 18 caracteres (tiene ${v.length}).` };
  }
  if (!CURP_REGEX.test(v)) {
    return { valid: false, reason: 'CURP no cumple el formato oficial (DOF). Verifica letras, fecha y entidad.' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   RFC — 12 (Persona Moral) o 13 (Persona Física) caracteres
   ──────────────────────────────────────────────────────────────────────── */

// PF: 4 letras (incluye Ñ y &) + AAMMDD + 3 alfanuméricos
const RFC_PF_REGEX = /^[A-ZÑ&]{4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/;
// PM: 3 letras + AAMMDD + 3 alfanuméricos
const RFC_PM_REGEX = /^[A-ZÑ&]{3}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/;

export function normalizeRFC(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).trim().toUpperCase().replace(/[\s-]/g, '');
}

export function validateRFC(input: string | null | undefined): ValidationResult {
  const v = normalizeRFC(input);
  if (!v) return OK;
  if (v.length !== 12 && v.length !== 13) {
    return { valid: false, reason: `RFC debe tener 12 (moral) o 13 (física) caracteres (tiene ${v.length}).` };
  }
  if (v.length === 13 && !RFC_PF_REGEX.test(v)) {
    return { valid: false, reason: 'RFC persona física no cumple formato. Esperado: 4 letras + AAMMDD + 3 alfanuméricos.' };
  }
  if (v.length === 12 && !RFC_PM_REGEX.test(v)) {
    return { valid: false, reason: 'RFC persona moral no cumple formato. Esperado: 3 letras + AAMMDD + 3 alfanuméricos.' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   CLABE — 18 dígitos + checksum Banxico
   Algoritmo (Circular 4/2003):
     pesos = [3,7,1, 3,7,1, 3,7,1, 3,7,1, 3,7,1, 3,7]
     suma  = sum(digit_i * peso_i % 10) for i in 0..16
     dv    = (10 - suma % 10) % 10
     comparar con dígito 18.
   ──────────────────────────────────────────────────────────────────────── */

const CLABE_WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7] as const;

export function normalizeCLABE(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).trim().replace(/[\s-]/g, '');
}

export function validateCLABE(input: string | null | undefined): ValidationResult {
  const v = normalizeCLABE(input);
  if (!v) return OK;
  if (v.length !== 18) {
    return { valid: false, reason: `CLABE debe tener exactamente 18 dígitos (tiene ${v.length}).` };
  }
  if (!/^\d{18}$/.test(v)) {
    return { valid: false, reason: 'CLABE solo puede contener dígitos.' };
  }
  // Checksum Banxico
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    // i < 17 garantiza CLABE_WEIGHTS[i] existe; el cast como number aplaca
    // a TS sin perder corrección (el array tiene exactamente 17 elementos).
    const w = CLABE_WEIGHTS[i] as number;
    sum += (Number(v[i]) * w) % 10;
  }
  const expectedDv = (10 - (sum % 10)) % 10;
  const actualDv = Number(v[17]);
  if (expectedDv !== actualDv) {
    return { valid: false, reason: 'CLABE inválida: dígito verificador no coincide. Verifica con la carátula bancaria.' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   Clave de Elector INE — 18 caracteres
   Estructura: 6 consonantes + AAMMDD + entidad(2) + H/M + 3 dígitos
   Esta es la clave alfanumérica del FRENTE de la credencial.
   No confundir con CIC (9 dígitos atrás) ni OCR (13 dígitos atrás).
   ──────────────────────────────────────────────────────────────────────── */

const INE_REGEX = /^[A-Z]{6}\d{8}[HM]\d{3}$/;

export function normalizeClaveElector(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).trim().toUpperCase().replace(/\s+/g, '');
}

export function validateClaveElector(input: string | null | undefined): ValidationResult {
  const v = normalizeClaveElector(input);
  if (!v) return OK;
  if (v.length !== 18) {
    return { valid: false, reason: `Clave de elector INE debe tener 18 caracteres (tiene ${v.length}).` };
  }
  if (!INE_REGEX.test(v)) {
    return { valid: false, reason: 'Clave de elector no cumple formato. Esperado: 6 letras + 8 dígitos + H/M + 3 dígitos.' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   Teléfono México — 10 dígitos (post Plan Nacional 2019)
   Aceptamos +52 prefix opcional, espacios, guiones y paréntesis. La
   normalización deja solo dígitos (sin +52).
   ──────────────────────────────────────────────────────────────────────── */

export function normalizeTelefonoMx(input: string | null | undefined): string {
  if (!input) return '';
  // Quitar todo menos dígitos. Si vino con +52 o 52 al inicio (12 dígitos
  // total), removerlo para quedar en 10.
  let digits = String(input).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('52')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1); // legacy 044/045
  return digits;
}

export function validateTelefonoMx(input: string | null | undefined): ValidationResult {
  if (!input || !String(input).trim()) return OK;
  const digits = normalizeTelefonoMx(input);
  if (digits.length !== 10) {
    return { valid: false, reason: `Teléfono debe tener 10 dígitos (recibí ${digits.length}). Ej: 442 123 4567.` };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   Email — usamos regex pragmático (no RFC 5322 completo)
   ──────────────────────────────────────────────────────────────────────── */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalizeEmail(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).trim().toLowerCase();
}

export function validateEmail(input: string | null | undefined): ValidationResult {
  const v = normalizeEmail(input);
  if (!v) return OK;
  if (!EMAIL_REGEX.test(v)) {
    return { valid: false, reason: 'Correo electrónico inválido. Formato: usuario@dominio.com' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   Folios RAN — formato federal `^[0-9]{2}(TM|FD)[0-9]{8}$` (12 chars).
   2 dígitos entidad INEGI (Querétaro=22) + TM/FD + 8 dígitos.
   El RAN garantiza unicidad cross-tipo: TM=Folio Tierras, FD=Folio Derechos.
   ──────────────────────────────────────────────────────────────────────── */

const FOLIO_RAN_REGEX = /^[0-9]{2}(TM|FD)[0-9]{8}$/;

export function normalizeFolioRan(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).replace(/[\s-]/g, '').toUpperCase().trim();
}

export function validateFolioDerechos(input: string | null | undefined): ValidationResult {
  const v = normalizeFolioRan(input);
  if (!v) return OK;
  if (!FOLIO_RAN_REGEX.test(v)) {
    return { valid: false, reason: 'Folio derechos: 12 chars formato RAN (ej. 22FD00059032). 2 dígitos entidad + TM/FD + 8 dígitos.' };
  }
  return OK;
}

export function validateFolioTierras(input: string | null | undefined): ValidationResult {
  const v = normalizeFolioRan(input);
  if (!v) return OK;
  if (!FOLIO_RAN_REGEX.test(v)) {
    return { valid: false, reason: 'Folio tierras: 12 chars formato RAN (ej. 22TM00012345). 2 dígitos entidad + TM/FD + 8 dígitos.' };
  }
  return OK;
}

/* ════════════════════════════════════════════════════════════════════════
   Clave catastral municipal — sin estándar federal. Querétaro/Corregidora
   usa ~13 alfanuméricos (ej. F14C75I1349AD). Aceptamos 8–20 chars
   uppercase + alfanuméricos para cubrir variantes municipales.
   ──────────────────────────────────────────────────────────────────────── */

const CLAVE_CATASTRAL_REGEX = /^[A-Z0-9]{8,20}$/;

export function normalizeClaveCatastral(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).replace(/[\s-]/g, '').toUpperCase().trim();
}

export function validateClaveCatastral(input: string | null | undefined): ValidationResult {
  const v = normalizeClaveCatastral(input);
  if (!v) return OK;
  if (!CLAVE_CATASTRAL_REGEX.test(v)) {
    return { valid: false, reason: 'Clave catastral: 8–20 caracteres alfanuméricos (ej. F14C75I1349AD).' };
  }
  return OK;
}
